import { FC, useState } from "react";
import {
  ClickAwayListener,
  Box,
  BoxProps,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { DayPicker, DateRange } from "react-day-picker";
import "../styles/calendar.css";
import { subDays, startOfDay, startOfToday } from "date-fns";

export type CALENDAR_DATE = Date | DateRange | undefined;

export type CalendarProps = {
  mode: "range" | "single";
  selected?: DateRange | Date;
  onChange: (val?: DateRange | Date) => void;
  setShowCalendar?: (val: boolean) => void;
  minDate?: Date;
  defaultMonth?: Date;
  containerProps?: BoxProps;
  defaultQuickDays?: number | null;
  enableQuickSelect?: boolean;
  disableFutureDates?: boolean;
};

const Calendar: FC<CalendarProps> = ({
  mode,
  selected,
  onChange,
  setShowCalendar,
  minDate,
  defaultMonth,
  containerProps,
  defaultQuickDays,
  enableQuickSelect,
  disableFutureDates = false,
}) => {
  const [quickDays, setQuickDays] = useState<number | null>(
    defaultQuickDays ?? null
  );

  const handleSelect = (val: any) => {
    if (mode === "single" && val) {
      const isEarlierDate =
        minDate &&
        new Date(val.setHours(0, 0, 0, 0)) <
          new Date(minDate.setHours(0, 0, 0, 0));

      if (!isEarlierDate) {
        onChange(val);
        setShowCalendar?.(false);
      }
    }

    if (mode === "range") {
      onChange(val);
    }
  };

  const validRange = disableFutureDates
    ? {
        after: startOfToday(),
      }
    : undefined;

  return (
    <ClickAwayListener onClickAway={() => setShowCalendar?.(false)}>
      <Box
        minWidth={320}
        p={2}
        borderRadius={2}
        bgcolor="custom.background.primary"
        zIndex={1300}
        {...containerProps}
        sx={{
          boxShadow:
            "0px 1px 4px rgba(0, 0, 0, 0.12), 0px 0px 1px rgba(0, 0, 0, 0.04)",
          ...containerProps?.sx,
        }}
      >
        {mode === "range" ? (
          <DayPicker
            mode="range"
            captionLayout="dropdown"
            selected={selected as DateRange}
            onSelect={handleSelect}
            numberOfMonths={1}
            showOutsideDays
            required
            defaultMonth={
              defaultMonth ||
              (selected && typeof selected === "object" && "from" in selected
                ? selected.from
                : new Date())
            }
          />
        ) : (
          <DayPicker
            mode="single"
            captionLayout="dropdown"
            selected={selected as Date}
            onSelect={handleSelect}
            numberOfMonths={1}
            showOutsideDays
            defaultMonth={defaultMonth || (selected as Date) || new Date()}
            disabled={validRange}
          />
        )}

        {enableQuickSelect && (
          <Box mt={2} display="flex" gap={1}>
            {[7, 15, 30].map((days) => (
              <FormControlLabel
                key={days}
                control={
                  <Checkbox
                    size="small"
                    checked={quickDays === days}
                    onChange={() => {
                      const isChecked = quickDays === days;
                      if (isChecked) {
                        setQuickDays(null);
                        onChange(undefined);
                      } else {
                        const end = startOfDay(new Date());
                        const start = subDays(end, days - 1);
                        setQuickDays(days);
                        onChange({ from: start, to: end });
                      }
                    }}
                    sx={{
                      "&.Mui-checked": {
                        color: "custom.background.brand",
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "13px" }}>
                    Last {days} Days
                  </Typography>
                }
              />
            ))}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default Calendar;
